// =============================================================================
// domain/patternVersions.ts — a pattern's rule lifecycle.
// -----------------------------------------------------------------------------
// This is the machinery behind "Spot it. Fix it. Prove it.", and the reason the
// Setups editor could not just be a form over a row.
//
// ── 🚨 THE ONE RULE EVERYTHING ELSE SERVES ──
//
// **Editing a PRODUCTION pattern's rules never changes the live version.**
//
// A trade is PINNED, at tag time, to the version that was live then
// (`trade.patternVersion` — already ported into the trade form). So the live
// version is the yardstick a year of trades has already been measured against.
// Rewriting it retroactively re-judges all of them against rules written
// afterwards: no error, no visible change, and every "did I follow my rules?"
// number silently becomes about a different question.
//
// Instead, edits accumulate in a CANDIDATE version. Promote it and it becomes the
// new live one, superseding the old; reject it and the working rules snap back.
//
// ── TWO DIFFERENT "PROMOTE"S, AND CONFLATING THEM IS A BUG ──
//
//   promoteToProduction  a candidate PATTERN becomes taggable on live trades.
//                        `status: 'candidate' → 'production'`. Nothing to do with
//                        versions — it is the "▲ Promote to production" button on
//                        a card with no sample yet.
//   promoteVersion       a candidate VERSION becomes the live one. Old live →
//                        `super`, candidate → `live`. Nothing to do with `status`.
//
// ── PURE AND IMMUTABLE, WHERE THE OLD CLIENT MUTATES ──
//
// Every function here returns new state and touches nothing. The old client
// mutates the pattern in place and relies on `saveState()` afterwards, which is
// why its reconcile can leave a half-updated object if anything between throws.
// `domain/` cannot reach a clock either, so `now` is a parameter.
// =============================================================================

/** One entry-criterion — a checkbox on the trade form when the pattern is tagged. */
export interface Criterion {
  id: string;
  label: string;
}

/**
 * ⚠ `super` = superseded by a newer live version; `rej` = abandoned, kept for the
 * history. Both are terminal, and both stay in `versions[]` — a rejected rule set
 * is evidence about what did not work, which is the whole point of the loop.
 */
export type VersionStatus = 'live' | 'candidate' | 'super' | 'rej';

export interface PatternVersion {
  v: number;
  createdAt: string | number;
  status: VersionStatus;
  entryCriteria: Criterion[];
  invalidation: string;
  /** Why a candidate was abandoned. Only on `rej`. */
  reason?: string;
}

/** A pattern's WORKING rules — what the editor shows and edits. */
export interface WorkingRules {
  entryCriteria: Criterion[];
  invalidation: string;
}

/** The lifecycle fields, all of which ride `extras`. */
export interface PatternLifecycle {
  status: string;
  versions: PatternVersion[];
  liveVersion: number;
  candidateVersion: number | null;
  createdAt?: string | number;
}

/** A pattern as this module sees it: working rules + lifecycle. */
export interface VersionedPattern extends PatternLifecycle, WorkingRules {}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

const criteriaOf = (v: unknown): Criterion[] =>
  Array.isArray(v)
    ? (v as unknown[]).flatMap((c) => {
      if (!c || typeof c !== 'object') return [];
      const r = c as Record<string, unknown>;
      const id = typeof r['id'] === 'string' ? r['id'] : '';
      return id ? [{ id, label: typeof r['label'] === 'string' ? r['label'] : '' }] : [];
    })
    : [];

/**
 * The id for a newly typed criterion — a slug of its label, uniquified.
 *
 * ⚠ Ported from the old client's `slugifyConfluence`, which the pattern form calls
 * for exactly this. The id is not cosmetic: it is what a trade's `criteriaMet` map
 * is keyed by, so a criterion whose id changes loses every tick recorded against
 * it. Hence the editor uniquifies only NEW labels — an existing criterion keeps the
 * id it was stored with even after its text is edited.
 *
 * ⚠ The `'criterion'` fallback is unreachable from the editor, which refuses to add
 * a blank label. It is here because the slug of `"!!!"` is the empty string, and two
 * empty ids would silently merge two criteria into one.
 */
export function criterionId(label: string, existingIds: readonly string[]): string {
  const base = (label || 'criterion').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'criterion';
  let id = base;
  let n = 2;
  while (existingIds.includes(id)) id = `${base}-${n++}`;
  return id;
}

/** The working rules, normalised. */
export function snapshotRules(p: Partial<VersionedPattern> | null | undefined): WorkingRules {
  return {
    entryCriteria: clone(criteriaOf(p?.entryCriteria)),
    invalidation: typeof p?.invalidation === 'string' ? p.invalidation : '',
  };
}

/**
 * The version list, ALWAYS non-empty.
 *
 * ⚠ A pattern with no `versions[]` gets a synthesised live v1 from its current
 * rules rather than an empty list. Every caller then has a version to compare
 * against, so none of them needs an "unversioned pattern" branch — and it is that
 * missing branch, not the lifecycle itself, that would produce a live version of
 * `undefined` and a reconcile that decides nothing changed.
 */
export function patternVersions(p: Partial<VersionedPattern> | null | undefined): PatternVersion[] {
  if (Array.isArray(p?.versions) && p.versions.length) return p.versions;
  return [{
    v: 1,
    createdAt: p?.createdAt ?? 0,
    status: 'live',
    ...snapshotRules(p),
  }];
}

export function versionByNum(
  p: Partial<VersionedPattern> | null | undefined, v: number | null | undefined,
): PatternVersion | null {
  if (v == null) return null;
  return patternVersions(p).find((x) => x.v === v) ?? null;
}

/**
 * ⚠ Three fallbacks, in the old client's order: the numbered live version, then
 * ANY version marked live, then the first. A pattern whose `liveVersion` points
 * at a deleted number still has a yardstick — losing one silently un-pins every
 * trade that referenced it.
 */
export function liveVersionOf(p: Partial<VersionedPattern> | null | undefined): PatternVersion | null {
  const vs = patternVersions(p);
  const lv = p?.liveVersion ?? 1;
  return vs.find((x) => x.v === lv) ?? vs.find((x) => x.status === 'live') ?? vs[0] ?? null;
}

export function candidateVersionOf(
  p: Partial<VersionedPattern> | null | undefined,
): PatternVersion | null {
  if (p?.candidateVersion == null) return null;
  return versionByNum(p, p.candidateVersion);
}

/**
 * A "rule change" is a difference in entry criteria (ORDER-SENSITIVE) or in the
 * invalidation.
 *
 * ⚠ Order-sensitive deliberately: the criteria are a checklist a trader reads
 * top-down, and reordering them changes the checklist. Treating a reorder as
 * "no change" would leave the live version showing a different order from the one
 * on screen.
 */
export function rulesEqual(a: WorkingRules, b: WorkingRules): boolean {
  return JSON.stringify(a.entryCriteria) === JSON.stringify(b.entryCriteria)
    && a.invalidation === b.invalidation;
}

/**
 * Bring the version model back into agreement with the working rules. Run after
 * every rule edit.
 *
 * PRODUCTION → the live version is untouchable. Divergence goes into a candidate
 * (created, or updated if one is already in flight); reverting drops it.
 * CANDIDATE  → there is no frozen live version to protect, so its own v1 tracks
 * the working rules directly and no separate candidate is held.
 */
export function reconcileVersions(p: VersionedPattern, now: string): PatternLifecycle {
  const versions = clone(patternVersions(p));
  const liveVersion = p.liveVersion ?? 1;
  const cur = snapshotRules(p);
  const live = versions.find((x) => x.v === liveVersion)
    ?? versions.find((x) => x.status === 'live')
    ?? versions[0];

  const base: PatternLifecycle = {
    status: p.status,
    versions,
    liveVersion: live?.v ?? 1,
    candidateVersion: p.candidateVersion ?? null,
    ...(p.createdAt !== undefined ? { createdAt: p.createdAt } : {}),
  };

  if (p.status === 'production') {
    // Unchanged, or nothing to compare against → no candidate should exist.
    if (!live || rulesEqual(cur, live)) return dropCandidate(base);

    const cand = base.candidateVersion != null
      ? versions.find((x) => x.v === base.candidateVersion)
      : undefined;

    if (cand) {
      cand.entryCriteria = cur.entryCriteria;
      cand.invalidation = cur.invalidation;
      return base;
    }
    // ⚠ Numbered by MAX(v) + 1, never by the array length — a rejected version
    // stays in the list, so length and the next number diverge the moment one is.
    const nv = versions.reduce((m, x) => Math.max(m, x.v), 0) + 1;
    versions.push({
      v: nv, createdAt: now, status: 'candidate',
      entryCriteria: cur.entryCriteria, invalidation: cur.invalidation,
    });
    return { ...base, candidateVersion: nv };
  }

  // A candidate PATTERN: keep its own live version in step, hold no candidate.
  if (live) {
    live.entryCriteria = cur.entryCriteria;
    live.invalidation = cur.invalidation;
  }
  return dropCandidate(base);
}

/** Remove the in-flight candidate version, if any. */
function dropCandidate(s: PatternLifecycle): PatternLifecycle {
  if (s.candidateVersion == null) return s;
  return {
    ...s,
    versions: s.versions.filter((x) => x.v !== s.candidateVersion),
    candidateVersion: null,
  };
}

/**
 * A candidate PATTERN becomes taggable on live trades.
 *
 * ⚠ Only touches `status`. It is not a version operation, and the button that
 * calls it sits on a card with no sample — the two promotes look alike in the UI
 * and are unrelated in the data.
 */
export function promoteToProduction(p: PatternLifecycle): PatternLifecycle {
  if (p.status !== 'candidate') return p;
  return { ...p, status: 'production' };
}

/**
 * The candidate VERSION becomes live; the old live is superseded.
 *
 * ⚠ The old live becomes `super`, not deleted. Trades pinned to it must still
 * resolve their criteria — that pin is the whole reason this subsystem exists.
 */
export function promoteVersion(p: PatternLifecycle): PatternLifecycle {
  if (p.candidateVersion == null) return p;
  const versions = clone(patternVersions(p));
  const cand = versions.find((x) => x.v === p.candidateVersion);
  if (!cand) return p;
  const oldLive = versions.find((x) => x.v === p.liveVersion)
    ?? versions.find((x) => x.status === 'live');
  if (oldLive) oldLive.status = 'super';
  cand.status = 'live';
  return { ...p, versions, liveVersion: cand.v, candidateVersion: null };
}

/**
 * Abandon the candidate version.
 *
 * ⚠ Returns the reverted WORKING RULES too, and that is not a convenience: the
 * candidate held the user's edits, so dropping it without restoring the live
 * version's rules leaves the editor showing rules that exist in no version at
 * all — and the next reconcile would immediately re-create the candidate they
 * just rejected.
 */
export function rejectVersion(
  p: PatternLifecycle, reason: string,
): { lifecycle: PatternLifecycle; rules: WorkingRules } {
  const fallback = { lifecycle: p, rules: snapshotRules(p as VersionedPattern) };
  if (p.candidateVersion == null) return fallback;

  const versions = clone(patternVersions(p));
  const cand = versions.find((x) => x.v === p.candidateVersion);
  if (!cand) return fallback;

  cand.status = 'rej';
  cand.reason = reason.trim() || `Rejected — abandoned in favor of live v${p.liveVersion}.`;

  const live = versions.find((x) => x.v === p.liveVersion);
  return {
    lifecycle: { ...p, versions, candidateVersion: null },
    rules: {
      entryCriteria: clone(live?.entryCriteria ?? []),
      invalidation: live?.invalidation ?? '',
    },
  };
}

/**
 * The criteria a TRADE is judged by.
 *
 * ⚠ The pin wins over the live version. That is the entire contract: a trade
 * tagged in May is scored against May's rules, not against whatever is live
 * today. A pin pointing at a version that no longer exists falls back to live —
 * degraded, but never blank, because an empty checklist reads as "no criteria"
 * rather than as "the version is missing".
 */
export function resolveCriteria(
  p: Partial<VersionedPattern> | null | undefined,
  pinnedVersion: number | null | undefined,
): Criterion[] {
  if (!p) return [];
  const ver = versionByNum(p, pinnedVersion) ?? liveVersionOf(p);
  if (ver && Array.isArray(ver.entryCriteria)) return ver.entryCriteria;
  return criteriaOf(p.entryCriteria);
}
